import type {
  FeatureRequestMessageKind,
  FeatureRequestStatus,
  PublicUser,
  LobbySettings,
  PresetCompat,
  OverlayNote,
  ScoredCandidate,
  GameStateView,
  PlaySlot,
} from '@srcbattle/shared';

/** Mirrors the server's stats service. Kept here so the client has no import
 *  from the server package — the two agree by review, not by coupling. */
export interface PersonStat {
  userId: string;
  displayName: string;
  username: string;
  avatarValue: string;
  gamesPlayed: number;
  gamesWon: number;
  entriesSubmitted: number;
  matchesPlayed: number;
  matchesWon: number;
  matchWinRate: number | null;
  votesReceived: number;
  votesCast: number;
  votesPerEntry: number | null;
  finalsReached: number;
  finalsLost: number;
  firstRoundExits: number;
  longestWinStreak: number;
  wonByOne: number;
  lostByOne: number;
  listeningMs: number;
  nemesis: { userId: string; displayName: string; count: number } | null;
  twin: { userId: string; displayName: string; count: number } | null;
  kingmaker: number;
}

export interface StatsResponse {
  people: PersonStat[];
  collective: {
    gamesPlayed: number;
    entriesSubmitted: number;
    matchesPlayed: number;
    votesCast: number;
    listeningMs: number;
    /** Solo games deliberately left out of the numbers above. */
    soloGamesExcluded: number;
    mostSubmitted: { title: string; times: number; wins: number; losses: number } | null;
  };
  emptyReason: string | null;
  recentGames: GameHistoryEntry[];
}

/** A saved copy of a lobby's settings. Mirrors the server's preset service. */
export interface SettingsPreset {
  id: string;
  name: string;
  description: string;
  settings: LobbySettings;
  /** The site version label in force when the preset was last saved. */
  appVersion: string;
  /** Whether the saved snapshot still matches this build's settings page. */
  compat: PresetCompat;
  isPublished: boolean;
  publishedAt: number | null;
  createdAt: number;
  updatedAt: number;
  authorId: string;
  authorName: string;
  averageRating: number | null;
  ratingCount: number;
  myRating: number | null;
  isMine: boolean;
}

export type PresetSort = 'rating' | 'newest' | 'name' | 'author';

/** Mirrors the server's feature request service. */
export interface FeatureRequestMessage {
  id: string;
  kind: FeatureRequestMessageKind;
  body: string;
  authorId: string;
  authorName: string;
  /** Whether the member who asked wrote this, rather than an admin. */
  fromAuthor: boolean;
  createdAt: number;
}

export interface FeatureRequest {
  id: string;
  title: string;
  body: string;
  status: FeatureRequestStatus;
  createdAt: number;
  updatedAt: number;
  authorId: string;
  authorName: string;
  authorAvatar: string;
  isMine: boolean;
  messageCount: number;
  lastActivityAt: number;
  /** The other side has written since this viewer last opened the thread. */
  hasUnread: boolean;
  /** Null in a list; filled when a single thread is fetched. */
  messages: FeatureRequestMessage[] | null;
}

export interface HallOfFameWinner {
  gameId: string;
  gameName: string;
  endedAt: number;
  title: string;
  artist: string | null;
  sourceUrl: string;
  artworkUrl: string | null;
  submitterName: string;
}

export interface GameHistoryEntry {
  id: string;
  lobbyId: string;
  name: string;
  endedAt: number;
  championEntryId: string | null;
  people: Array<{ id: string; name: string }>;
  entries: Array<{ id: string; userId: string; title: string; artist: string | null; sourceUrl: string; clipMs: number; dedupeKey: string; artworkUrl: string | null }>;
  matches: Array<{ round: number; index: number; kind: string; a: string | null; b: string | null; winner: string | null; decidedBy: string | null; votes: Array<{ userId: string; entryId: string; votingRound: number }> }>;
  /** One submitter only: played for fun, and left out of the stats. */
  isSolo: boolean;
}

export interface ApiError {
  error: string;
  message: string;
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    // The session cookie is HttpOnly, so every call has to opt in to sending it.
    credentials: 'same-origin',
    // Only a JSON body gets the JSON header. A FormData body must be left
    // alone so the browser can set the multipart content type and, crucially,
    // the boundary — which we have no way of knowing here.
    headers: typeof init?.body === 'string' ? { 'content-type': 'application/json' } : undefined,
    ...init,
  });

  if (!response.ok) {
    let body: Partial<ApiError> = {};
    try {
      body = (await response.json()) as Partial<ApiError>;
    } catch {
      // A non-JSON error body means something upstream failed; fall through to
      // the generic message rather than surfacing a parse error to the member.
    }
    throw new ApiRequestError(
      response.status,
      body.error ?? 'request_failed',
      body.message ?? 'Something went wrong.',
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * A file upload.
 *
 * Separate from `post` because the content type must *not* be set: the browser
 * writes it itself, with the multipart boundary it generated. Setting it by
 * hand produces a request the server cannot parse, and the error it gives back
 * ("unsupported media type") points nowhere near the cause.
 */
function upload<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  return request<T>(path, { method: 'POST', body: form });
}

export const ASSET_SLOTS = ['logo', 'favicon', 'banner', 'homePhoto1', 'homePhoto2', 'homePhoto3'] as const;
export type AssetSlot = (typeof ASSET_SLOTS)[number];
export type SiteAssets = Record<AssetSlot, string | null>;
export type HomePlacement = 'top' | 'before_lobbies' | 'after_lobbies';
export interface HomeAppearance {
  message: string;
  messageSize: number;
  messagePlacement: HomePlacement;
  photos: Record<'homePhoto1' | 'homePhoto2' | 'homePhoto3', { placement: HomePlacement; align: 'left' | 'center' | 'right'; size: number }>;
}

function post<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

export interface SessionResponse {
  user: PublicUser;
  isAdmin: boolean;
  needsProfile: boolean;
  theme: string;
  isGuest: boolean;
  /** When a guest's access ends. Null for members. */
  guestExpiresAt: number | null;
}

/** Mirrors the server's guest service. */
export interface GuestInvite {
  id: string;
  createdAt: number;
  expiresAt: number;
  createdBy: { id: string; name: string };
  guest: { id: string; name: string; avatarValue: string; isSetUp: boolean } | null;
}

export interface AdminMember {
  id: string;
  /** The stable handle, which only an admin can change. */
  username: string;
  /** What they are shown as: their display name, or their username. */
  displayName: string;
  avatarValue: string;
  isAdmin: boolean;
  isSetUp: boolean;
  extraSlotsReceived: number;
  createdAt: number;
  lastSeenAt: number;
  codeLabel: string | null;
  hasActiveCode: boolean;
}

export interface AdminLobby {
  id: string;
  code: string;
  name: string;
  status: string;
  hostName: string;
  memberCount: number;
  connectedCount: number;
  createdAt: number;
}

export interface LobbyMemberView {
  userId: string;
  displayName: string;
  avatarValue: string;
  role: 'host' | 'player' | 'spectator';
  slotsAllotted: number;
  /** The host's fixed count, or null when the planner decides it. */
  slotsOverride: number | null;
  isConnected: boolean;
  submittedCount: number;
}

export interface SlotPlanOk {
  ok: true;
  base: number;
  extraCount: number;
  /** How many members the host has given a fixed count. */
  customCount: number;
  summary: string;
}
export interface SlotPlanError {
  ok: false;
  reason: string;
  message: string;
}

export interface LobbyView {
  id: string;
  code: string;
  name: string;
  hostUserId: string;
  status: string;
  settings: LobbySettings;
  bracketSize: number;
  rngSeed: number;
  members: LobbyMemberView[];
  slotPlan: SlotPlanOk | SlotPlanError;
  totalSubmitted: number;
  totalRequired: number;
  createdAt: number;
}

export interface EntryView {
  id: string;
  title: string;
  artist: string | null;
  /** Which player the clip picker should mount. */
  source: 'youtube' | 'spotify';
  youtubeVideoId: string | null;
  spotifyTrackId: string | null;
  durationMs: number;
  thumbnailUrl: string | null;
  clipStartMs: number;
  clipEndMs: number;
  /** Whether this lobby lets the submitter pick the start point. */
  canChooseClip: boolean;
  /** Zero when the length is unknown; the picker asks the player instead. */
  maxClipStartMs: number;
  clipLengthMs: number;
  createdAt: number;
}

export interface Capabilities {
  youtubeBasic: boolean;
  youtubeDetails: boolean;
  spotifyMatching: boolean;
}

export interface LibrarySong {
  id: string;
  source: 'youtube' | 'spotify';
  sourceUrl: string;
  providerId: string;
  title: string;
  artist: string | null;
  durationMs: number;
  thumbnailUrl: string | null;
  groupName: string | null;
  clipStartMs: number;
  createdAt: number;
}

export type SubmitResponse =
  | { status: 'added'; entry: EntryView }
  | {
      status: 'needs_choice';
      track: {
        trackId: string;
        title: string;
        artist: string;
        durationMs: number;
        albumArtUrl: string | null;
      };
      candidates: ScoredCandidate[];
    };

export const api = {
  health: () => request<{ ok: boolean; time: number }>('/api/health'),

  me: () => request<SessionResponse>('/api/me'),
  redeem: (code: string) => post<SessionResponse>('/api/auth/redeem', { code }),
  adminRecover: (password: string) => post<SessionResponse>('/api/auth/admin-recover', { password }),
  redeemGuest: (token: string) => post<SessionResponse>('/api/auth/guest', { token }),

  guestInvites: () => request<{ invites: GuestInvite[] }>('/api/guest-invites'),
  createGuestInvite: () => post<{ token: string; invite: GuestInvite }>('/api/guest-invites'),
  revokeGuestInvite: (id: string) => request<{ ok: true }>(`/api/guest-invites/${id}`, { method: 'DELETE' }),
  adminGuests: () => request<{ invites: GuestInvite[] }>('/api/admin/guests'),
  logout: () => post<{ ok: true }>('/api/auth/logout'),

  saveProfile: (input: { displayName: string; avatarValue: string; theme?: string }) =>
    post<SessionResponse>('/api/me/profile', input),
  saveTheme: (theme: string) => post<{ ok: true; theme: string }>('/api/me/theme', { theme }),

  library: () => request<{ songs: LibrarySong[] }>('/api/library'),
  addLibrarySong: (url: string) => post<{ song: LibrarySong }>('/api/library', { url }),
  setLibraryStart: (id: string, startMs: number, durationMs?: number) => request<{ song: LibrarySong }>(`/api/library/${id}`, { method: 'PATCH', body: JSON.stringify({ startMs, durationMs }) }),
  setLibraryGroup: (id: string, groupName: string | null) => request<{ song: LibrarySong }>(`/api/library/${id}`, { method: 'PATCH', body: JSON.stringify({ groupName }) }),
  removeLibrarySong: (id: string) => request<{ ok: true }>(`/api/library/${id}`, { method: 'DELETE' }),
  submitLibrarySong: (id: string, lobbyId: string) => post<SubmitResponse>(`/api/library/${id}/submit`, { lobbyId }),

  uploadAvatar: (file: File) =>
    upload<{ ok: true; user: PublicUser }>('/api/me/avatar', file),
  uploadReaction: (file: File) =>
    upload<{ ok: true; image: { url: string; bytes: number } }>('/api/reactions/image', file),
  setAvatarEmoji: (emoji: string) =>
    post<{ ok: true; user: PublicUser }>('/api/me/avatar/emoji', { emoji }),

  assets: () => request<{ assets: SiteAssets }>('/api/assets'),
  appearance: () => request<{ appearance: HomeAppearance }>('/api/appearance'),
  saveAppearance: (patch: Partial<HomeAppearance>) => request<{ appearance: HomeAppearance }>('/api/admin/appearance', { method: 'PATCH', body: JSON.stringify(patch) }),
  uploadAsset: (slot: AssetSlot, file: File) =>
    upload<{ ok: true; assets: SiteAssets }>(`/api/admin/assets/${slot}`, file),
  clearAsset: (slot: AssetSlot) =>
    request<{ ok: true; assets: SiteAssets }>(`/api/admin/assets/${slot}`, { method: 'DELETE' }),

  listMembers: () => request<{ members: AdminMember[] }>('/api/admin/members'),
  createMember: (label: string) =>
    post<{ code: string; userId: string; label: string }>('/api/admin/members', { label }),
  rotateCode: (userId: string) =>
    post<{ code: string; userId: string }>(`/api/admin/members/${userId}/rotate`),
  revokeCode: (userId: string) => post<{ ok: true }>(`/api/admin/members/${userId}/revoke`),
  renameMember: (userId: string, username: string) =>
    post<{ ok: true; username: string }>(`/api/admin/members/${userId}/username`, { username }),

  /** The settings every new lobby starts from. */
  adminSettings: () =>
    request<{ settings: LobbySettings; builtIn: LobbySettings }>('/api/admin/settings'),
  saveAdminSettings: (patch: Partial<LobbySettings>) =>
    request<{ settings: LobbySettings }>('/api/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  resetAdminSettings: () => post<{ settings: LobbySettings }>('/api/admin/settings/reset'),
  superReactionSetting: () => request<{ enabled: boolean; durationMs: number }>('/api/admin/super-reactions'),
  saveSuperReactionSetting: (patch: { enabled?: boolean; durationMs?: number }) => request<{ enabled: boolean; durationMs: number }>('/api/admin/super-reactions', { method: 'PATCH', body: JSON.stringify(patch) }),
  siteConfig: () => request<{ title: string }>('/api/site-config'),
  saveSiteTitle: (title: string) =>
    request<{ title: string }>('/api/admin/site-title', {
      method: 'PATCH',
      body: JSON.stringify({ title }),
    }),
  version: () => request<{ version: string }>('/api/version'),
  saveVersion: (version: string) =>
    request<{ version: string }>('/api/admin/version', {
      method: 'PATCH',
      body: JSON.stringify({ version }),
    }),
  adminLobbies: () => request<{ lobbies: AdminLobby[] }>('/api/admin/lobbies'),
  adminGameHistory: () => request<{ games: GameHistoryEntry[] }>('/api/admin/game-history'),

  // --- Lobbies -------------------------------------------------------------
  listLobbies: () =>
    request<{
      mine: Array<{ id: string; code: string; name: string; status: string; isHost: boolean }>;
      open: Array<{ id: string; code: string; name: string; status: string }>;
      capabilities: Capabilities;
    }>('/api/lobbies'),

  createLobby: (name: string) => post<LobbyView>('/api/lobbies', { name }),
  joinLobby: (code: string, asSpectator = false) =>
    post<LobbyView>('/api/lobbies/join', { code, asSpectator }),
  getLobby: (id: string) => request<LobbyView>(`/api/lobbies/${id}`),
  leaveLobby: (id: string) => post<{ ok: true }>(`/api/lobbies/${id}/leave`),
  deleteLobby: (id: string) =>
    request<{ ok: true }>(`/api/lobbies/${id}`, { method: 'DELETE' }),

  updateSettings: (id: string, patch: Partial<LobbySettings>) =>
    request<{ settings: LobbySettings; lobby: LobbyView }>(`/api/lobbies/${id}/settings`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  setMemberRole: (id: string, userId: string, role: 'player' | 'spectator') =>
    post<LobbyView>(`/api/lobbies/${id}/members/${userId}/role`, { role }),
  /** `slots: null` puts the member back on the automatic share. */
  setMemberSlots: (id: string, userId: string, slots: number | null) =>
    post<{ slots: number | null; entriesRemoved: number; lobby: LobbyView }>(
      `/api/lobbies/${id}/members/${userId}/slots`,
      { slots },
    ),

  // --- Entries -------------------------------------------------------------
  myEntries: (lobbyId: string) =>
    request<{ entries: EntryView[] }>(`/api/lobbies/${lobbyId}/entries`),

  submitEntry: (lobbyId: string, url: string) =>
    post<SubmitResponse>(`/api/lobbies/${lobbyId}/entries`, { url }),

  confirmEntry: (lobbyId: string, spotifyTrackId: string, youtubeVideoId: string) =>
    post<{ status: 'added'; entry: EntryView }>(`/api/lobbies/${lobbyId}/entries/confirm`, {
      spotifyTrackId,
      youtubeVideoId,
    }),

  setClipStart: (lobbyId: string, entryId: string, startMs: number, durationMs?: number) =>
    request<{ entry: EntryView }>(`/api/lobbies/${lobbyId}/entries/${entryId}/clip`, {
      method: 'PATCH',
      body: JSON.stringify({ startMs, durationMs }),
    }),
  removeEntry: (lobbyId: string, entryId: string) =>
    request<{ ok: true }>(`/api/lobbies/${lobbyId}/entries/${entryId}`, { method: 'DELETE' }),

  // --- Game ----------------------------------------------------------------
  getGame: (lobbyId: string) => request<GameStateView>(`/api/lobbies/${lobbyId}/game`),
  startGame: (lobbyId: string) => post<GameStateView>(`/api/lobbies/${lobbyId}/game/start`),

  play: (lobbyId: string, slot: PlaySlot) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/play`, { slot }),
  pause: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/pause`),
  resume: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/resume`),
  seek: (lobbyId: string, positionMs: number) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/seek`, { positionMs }),
  skip: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/skip`),
  /** Fired by the operator's player when it really starts, so the clock is honest. */
  playbackStarted: (lobbyId: string, positionMs: number) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/started`, { positionMs }),
  openVoting: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/open-voting`),
  /** The operator's player reporting where it really is. */
  fallbackToYouTube: (lobbyId: string, entryId: string) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/entries/${entryId}/fallback`),

  reportPosition: (lobbyId: string, positionMs: number, isPlaying: boolean) =>
    post<{ ok: true; changed: boolean }>(`/api/lobbies/${lobbyId}/game/position`, {
      positionMs,
      isPlaying,
    }),
  takeOver: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/take-over`),

  vote: (lobbyId: string, entryId: string) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/vote`, { entryId }),
  guess: (lobbyId: string, entryId: string, guessedUserId: string) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/guess`, { entryId, guessedUserId }),
  nudge: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/nudge`),
  closeVoting: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/close-voting`),
  decide: (lobbyId: string, entryId: string) =>
    post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/decide`, { entryId }),
  advance: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/advance`),
  voteToAdvance: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/advance-vote`),
  coinToss: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/game/coin-toss`),
  setCommunication: (lobbyId: string, patch: { reactionsEnabled?: boolean; chatEnabled?: boolean; superReactionsEnabled?: boolean }) =>
    request<{ ok: true }>(`/api/lobbies/${lobbyId}/game/communication`, { method: 'PATCH', body: JSON.stringify(patch) }),
  resetLobby: (lobbyId: string) => post<{ ok: true }>(`/api/lobbies/${lobbyId}/reset`),

  // --- Settings presets ------------------------------------------------------
  presets: () => request<{ presets: SettingsPreset[] }>('/api/presets'),
  publishedPresets: (options: { query?: string; sort?: PresetSort; authorId?: string } = {}) => {
    const params = new URLSearchParams();
    if (options.query) params.set('q', options.query);
    if (options.sort) params.set('sort', options.sort);
    if (options.authorId) params.set('author', options.authorId);
    const query = params.toString();
    return request<{ presets: SettingsPreset[] }>(`/api/presets/published${query ? `?${query}` : ''}`);
  },
  createPreset: (input: { name: string; description?: string; settings: LobbySettings }) =>
    post<{ preset: SettingsPreset }>('/api/presets', input),
  updatePreset: (id: string, patch: { name?: string; description?: string; settings?: LobbySettings }) =>
    request<{ preset: SettingsPreset }>(`/api/presets/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  /** Rewrites a flagged preset against the current settings page. Author only. */
  reconcilePreset: (id: string) => post<{ preset: SettingsPreset }>(`/api/presets/${id}/reconcile`),
  publishPreset: (id: string, input: { published: boolean; name?: string; description?: string }) =>
    post<{ preset: SettingsPreset }>(`/api/presets/${id}/publish`, input),
  ratePreset: (id: string, rating: number) =>
    post<{ preset: SettingsPreset }>(`/api/presets/${id}/rate`, { rating }),
  deletePreset: (id: string) => request<{ ok: true }>(`/api/presets/${id}`, { method: 'DELETE' }),
  applyPreset: (lobbyId: string, presetId: string) =>
    post<{ settings: LobbySettings; lobby: LobbyView }>(`/api/lobbies/${lobbyId}/settings/preset`, {
      presetId,
    }),

  // --- Stats -----------------------------------------------------------------
  stats: () => request<StatsResponse>('/api/stats'),
  hallOfFame: () => request<{ winners: HallOfFameWinner[] }>('/api/hall-of-fame'),

  // --- Feature requests ------------------------------------------------------
  featureRequests: () =>
    request<{ requests: FeatureRequest[]; unread: number; isAdmin: boolean }>('/api/feature-requests'),
  /** Just the badge count, for the header to poll. */
  featureRequestUnread: () => request<{ unread: number }>('/api/feature-requests/unread'),
  featureRequest: (id: string) => request<{ request: FeatureRequest }>(`/api/feature-requests/${id}`),
  createFeatureRequest: (input: { title: string; body: string }) =>
    post<{ request: FeatureRequest }>('/api/feature-requests', input),
  replyToFeatureRequest: (id: string, body: string) =>
    post<{ request: FeatureRequest }>(`/api/feature-requests/${id}/messages`, { body }),
  markFeatureRequestSeen: (id: string) =>
    post<{ ok: true; unread: number }>(`/api/feature-requests/${id}/seen`),
  deleteFeatureRequest: (id: string) =>
    request<{ ok: true }>(`/api/feature-requests/${id}`, { method: 'DELETE' }),
  adminFeatureRequests: (status?: FeatureRequestStatus) =>
    request<{ requests: FeatureRequest[] }>(
      `/api/admin/feature-requests${status ? `?status=${status}` : ''}`,
    ),
  setFeatureRequestStatus: (id: string, status: FeatureRequestStatus, note?: string) =>
    request<{ request: FeatureRequest }>(`/api/admin/feature-requests/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status, ...(note ? { note } : {}) }),
    }),

  // --- Operator overlay ------------------------------------------------------
  overlays: (lobbyId: string) =>
    request<{ notes: OverlayNote[] }>(`/api/lobbies/${lobbyId}/overlays`),
  addOverlay: (lobbyId: string, text: string) =>
    post<{ notes: OverlayNote[] }>(`/api/lobbies/${lobbyId}/overlays`, { text }),
  moveOverlay: (
    lobbyId: string,
    noteId: string,
    patch: { xPct?: number; yPct?: number; wPct?: number; fontScale?: number; text?: string },
  ) =>
    request<{ notes: OverlayNote[] }>(`/api/lobbies/${lobbyId}/overlays/${noteId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  removeOverlay: (lobbyId: string, noteId: string) =>
    request<{ notes: OverlayNote[] }>(`/api/lobbies/${lobbyId}/overlays/${noteId}`, {
      method: 'DELETE',
    }),

  mediaStatus: () =>
    request<{
      capabilities: Capabilities;
      notes: string[];
      quota: { units: number; limit: number; remaining: number; searchesRemaining: number; exhausted: boolean };
    }>('/api/admin/media'),
};
